import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Mock data for joined groups
const JOINED_GROUPS = [
  { id: 1, name: 'Photography', icon: '📷' },
  { id: 2, name: 'Web Development', icon: '💻' },
  { id: 3, name: 'Travel', icon: '✈️' },
  { id: 4, name: 'Music', icon: '🎵' },
  { id: 5, name: 'Gaming', icon: '🎮' },
];

// Mock data for trending topics
const TRENDING_TOPICS = [
  { id: 1, name: 'AI in everyday life', count: '2.5k posts' },
  { id: 2, name: 'Summer travel destinations', count: '1.8k posts' },
  { id: 3, name: 'New music releases', count: '1.2k posts' },
];

const SidebarContainer = styled.aside`
  width: 280px;
  background-color: ${({ theme }) => theme.colors.background};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.lg};
  height: calc(100vh - 70px); /* Adjust based on header height */
  position: sticky;
  top: 70px; /* Adjust based on header height */
  overflow-y: auto;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 240px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const SidebarSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.cardBg};
    padding-left: ${({ theme }) => theme.spacing.sm};
  }
`;

const GroupItem = styled(NavItem)`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const GroupIcon = styled.span`
  margin-right: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
`;

const TrendingItem = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} 0;
  cursor: pointer;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const TrendingName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const TrendingCount = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const ExploreButton = styled(Link)`
  display: block;
  text-align: center;
  background: ${({ theme }) => theme.gradients.secondary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  margin-top: ${({ theme }) => theme.spacing.md};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    color: white;
  }
`;

const Sidebar = () => {
  return (
    <SidebarContainer>
      <SidebarSection>
        <SectionTitle>Main Navigation</SectionTitle>
        <NavItem to="/">🏠 Home Feed</NavItem>
        <NavItem to="/discover">🔍 Discover Groups</NavItem>
        <NavItem to="/messages">✉️ Messages</NavItem>
        <NavItem to="/notifications">🔔 Notifications</NavItem>
        <NavItem to="/profile/me">👤 My Profile</NavItem>
      </SidebarSection>

      <SidebarSection>
        <SectionTitle>My Groups</SectionTitle>
        {JOINED_GROUPS.map(group => (
          <GroupItem key={group.id} to={`/group/${group.id}`}>
            <GroupIcon>{group.icon}</GroupIcon>
            {group.name}
          </GroupItem>
        ))}
        <ExploreButton to="/discover">Explore More Groups</ExploreButton>
      </SidebarSection>

      <SidebarSection>
        <SectionTitle>Trending Topics</SectionTitle>
        {TRENDING_TOPICS.map(topic => (
          <TrendingItem key={topic.id}>
            <TrendingName>{topic.name}</TrendingName>
            <TrendingCount>{topic.count}</TrendingCount>
          </TrendingItem>
        ))}
      </SidebarSection>
    </SidebarContainer>
  );
};

export default Sidebar;
